import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import Skeleton from "react-loading-skeleton";
import { getInitials, COUNTRY_OPTIONS, getGradeName } from "../../constants";
import { getOptionName } from "../../utils/getOptionName";
import { parseDate } from "../../utils/parseDate";
import PhotoLightbox from "../../components/PhotoLightbox";
import TournamentMenu from "./TournamentMenu";
import TournamentShareModal from "./export/TournamentShareModal";
import TournamentExportModal from "./export/TournamentExportModal";
import { ScreenshotButton } from "../../components/ScreenshotButton";
import { SCREENSHOT_TARGETS } from "./TournamentHeader";
import { openTournamentRating } from "../../utils/tournamentRatingBus";
import useAxios from "../../utils/useAxios";
import { useTheme } from "../../contexts/ThemeContext";
import { getAvailableResolutionPresets } from "../../utils/screenSizePresets";
import { isPreviewFrame } from "../../utils/previewFrame";
import BallIconMono from "../../components/BallIconMono";

// Same palette/rules as ClubProfileModal.js's own copy of this (itself
// matching TournamentsList.js's own copy) - kept as a third local copy
// rather than a shared import, consistent with how every other
// tournament-status reader in this codebase already does it.
const getTournamentStatus = (tournament) => {
    if (!tournament.start_date || !tournament.end_date) return "upcoming";
    const now = new Date();
    const start = parseDate(tournament.start_date);
    if (!start) return "upcoming";
    const end = parseDate(tournament.end_date);
    if (!end) return "upcoming";
    if (now < start) return "upcoming";
    if (now > end) return "completed";
    return "live";
};

const formatDateOnly = (value) => {
    const d = parseDate(value);
    if (!d) return "";
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
};

const formatDateRange = (startValue, endValue) => {
    const start = formatDateOnly(startValue);
    const end = formatDateOnly(endValue);
    if (!start) return end;
    if (!end || end === start) return start;
    return `${start} - ${end}`;
};

// Telegram-style hero for the tournament page, shown at every viewport
// width (see Tournament.js's own useTournamentHeroCollapse wiring) -
// structurally the same avatar-left/info-table-right shape as
// ClubProfileModal.js/PlayerProfileModal.js's own .profile-hero, including
// their own title-row's back-arrow/"..."-menu icons above the avatar+table -
// same duplication they already do between that row and their own sticky
// header's copy (TournamentHeader.js carries the same two controls, shown
// once this hero has scrolled past). goUp is lifted up to Tournament.js
// (rather than each of this and TournamentHeader.js computing its own
// arrivalActionRef) so both copies agree on where "back" actually goes.
const TournamentHero = ({
    tournament, heroSentinelRef, goUp,
    user, tournamentId, isAuthorized, onToggleSearch,
    theme, setTheme, accent, setAccent, compactTable, setCompactTable,
    language, handleLanguageChange
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const api = useAxios();
    const { previewWidth, setPreviewWidth } = useTheme();
    const [lightboxSrc, setLightboxSrc] = useState(null);
    const [ratingName, setRatingName] = useState(null);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [isExportOpen, setIsExportOpen] = useState(false);

    const data = tournament || {};

    // "At the top first" - this hero row (shown before any scroll, the
    // first thing a visitor sees) gets its own standalone Share/Export/
    // Screenshot icons instead of burying them in the "..." dropdown.
    // hideShareExport on the TournamentMenu call below drops Share/Export
    // from that dropdown here specifically, so they're not shown twice -
    // the sticky header's own copy (TournamentHeader.js) doesn't pass that
    // prop, so its "..." menu still carries them, same as it always has.
    const screenshotTarget = SCREENSHOT_TARGETS[new URLSearchParams(location.search).get("tab") || "information"];

    // "Screen size" preview - temporarily disabled, too buggy (blank-screen
    // flashes switching between presets) to leave reachable right now. The
    // underlying previewWidth is forced to 0 at the source (ThemeContext.js,
    // see its own comment) so this is belt-and-suspenders - without also
    // commenting the icon out here, it would still render and just silently
    // do nothing on tap (setPreviewWidth is a no-op now), which reads as a
    // broken button rather than a removed feature. Restore this block plus
    // ThemeContext.js's own commented-out line to bring it back.
    // const [deviceWidth, setDeviceWidth] = useState(() => window.innerWidth);
    // useEffect(() => {
    //     const onResize = () => setDeviceWidth(window.innerWidth);
    //     window.addEventListener("resize", onResize);
    //     return () => window.removeEventListener("resize", onResize);
    // }, []);
    // const availableResolutionPresets = getAvailableResolutionPresets(deviceWidth);
    // const resolutionIndex = Math.max(0, availableResolutionPresets.findIndex(preset => preset.width === previewWidth));
    // const activeResolutionPreset = availableResolutionPresets[resolutionIndex] || availableResolutionPresets[0];
    // const cyclePreviewWidth = () => {
    //     const nextIndex = (resolutionIndex + 1) % availableResolutionPresets.length;
    //     setPreviewWidth(availableResolutionPresets[nextIndex].width);
    // };

    // Tournament.rating is only the linked rating base's id - fetch its
    // name for display here (same lightweight /ratings/{id}/ endpoint
    // RatingForm.js already uses, not the heavier /table/ one RatingTable.js
    // needs for its own full standings fetch).
    useEffect(() => {
        if (!data.rating) {
            setRatingName(null);
            return;
        }
        let cancelled = false;
        api.get(`/api/v1.0/ratings/${data.rating}/`)
            .then(res => { if (!cancelled) setRatingName(res.data.name || null); })
            .catch(() => { if (!cancelled) setRatingName(null); });
        return () => { cancelled = true; };
    }, [data.rating]);

    // Same "open the club's own page" link as TournamentHeader.js/
    // TournamentInformation.js's own openClubModal - replace, not push, so
    // the header's back arrow isn't left having to undo an extra entry.
    const openClubModal = (e) => {
        e.preventDefault();
        if (!data.club) return;
        const params = new URLSearchParams(location.search);
        params.set("club_id", data.club);
        history.replace({ search: params.toString() });
    };

    // Same pattern as openClubModal above, opening App.js's always-mounted
    // LocationProfileModal (reads `location_id` off the querystring) instead
    // of ClubProfileModal.
    const openLocationModal = (e) => {
        e.preventDefault();
        if (!data.location) return;
        const params = new URLSearchParams(location.search);
        params.set("location_id", data.location);
        history.replace({ search: params.toString() });
    };

    const status = tournament ? getTournamentStatus(data) : null;
    const statusClass = status === "live" ? "in-progress" : status === "completed" ? "completed" : "expected";
    const statusIcon = statusClass === "in-progress" ? "activity" : statusClass === "completed" ? "check" : "clock";
    const statusLabel = status === "live" ? t("In progress") : status === "completed" ? t("Completed") : t("Expected");

    const locationLabel = [data.location_name, data.city, data.country ? t(getOptionName(COUNTRY_OPTIONS, data.country)) : null]
        .filter(Boolean)
        .join(" · ");

    // Extra facts (prize fund, surface, category, format) that today only
    // show up a tab away in Information - surfaced here too as a row of
    // chips below the existing Name/Club/Date/Location/Rating/Status table,
    // which stays as-is (no row removed/replaced).
    const countdownLabel = (() => {
        if (!tournament || status === "completed") return null;
        const dayMs = 24 * 60 * 60 * 1000;
        const now = new Date();
        if (status === "upcoming") {
            const start = parseDate(data.start_date);
            if (!start) return null;
            return t("tournament_starts_in_days", { count: Math.max(1, Math.ceil((start - now) / dayMs)) });
        }
        const end = parseDate(data.end_date);
        if (!end) return null;
        return t("tournament_ends_in_days", { count: Math.max(1, Math.ceil((end - now) / dayMs)) });
    })();

    const gradeLabel = data.grade
        ? [getGradeName(data.grade), data.gender ? t(`gender.${data.gender}`) : null].filter(Boolean).join(" · ")
        : null;

    // First chip replaces the table's old standalone Status row - same
    // badge classes/coloring (t-status-badge-sm) it used there, just with
    // the countdown folded in. The rest reuse that same badge look (via
    // the shared "expected" neutral variant) purely for a consistent pill
    // shape, not because they carry status meaning.
    const statusChipText = countdownLabel ? `${statusLabel} · ${countdownLabel}` : statusLabel;

    const factChips = tournament ? [
        { icon: statusIcon, text: statusChipText, badgeClass: statusClass },
        data.prize_fund && { icon: "gift", text: data.prize_fund, badgeClass: "expected" },
        data.court_surface && { icon: "layers", text: data.court_surface, badgeClass: "expected" },
        data.courts_count && { icon: "grid", text: t("courts_count_chip", { count: data.courts_count }), badgeClass: "expected" },
        gradeLabel && { icon: "tag", text: gradeLabel, badgeClass: "expected" },
        data.format && { icon: "sliders", text: data.format, badgeClass: "expected" },
        data.official_ball && { icon: "ball", text: data.official_ball, badgeClass: "expected" },
    ].filter(Boolean) : [];

    return (
        <div className="profile-hero">
            <div className="profile-hero-inner">
                {/* Scrolls away with the rest of the hero, same as club/player's
                    own hero-top row - TournamentHeader.js's sticky copy (its own
                    .tournament-header-top) takes over once scrolled past this. */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <a href="#" onClick={goUp} className="tournament-header-back" style={{ border: 0, color: 'var(--primary-color)' }}>
                        <FeatherIcon icon="arrow-left" size={20} />
                    </a>
                    <div className="tournament-header-right">
                        {/* Moved here from TournamentHeader.js's own sticky row -
                            that row used to render its own copy of this (plus the
                            back arrow and "..." menu) permanently at full opacity,
                            so scrolling to the top showed two back arrows and two
                            "..." menus stacked on top of each other instead of one
                            handing off to the other. Controls now live only here,
                            same as the hero's own back arrow/menu, instead of
                            duplicating into the sticky header at all. */}
                        {onToggleSearch && (
                            <a
                                href="#"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => { e.preventDefault(); onToggleSearch(); }}
                                className="tournament-header-search-toggle"
                                style={{ border: 0, color: 'var(--primary-color)' }}
                                title={t("Search")}
                            >
                                <FeatherIcon icon="search" size={16} />
                            </a>
                        )}
                        <a href="#" className="action-button matches-btn" onClick={(e) => { e.preventDefault(); setIsShareOpen(true); }} title={t("Share")}>
                            <FeatherIcon icon="share-2" size={16} />
                        </a>
                        <a href="#" className="action-button matches-btn" onClick={(e) => { e.preventDefault(); setIsExportOpen(true); }} title={t("Export")}>
                            <FeatherIcon icon="download" size={16} />
                        </a>
                        {screenshotTarget && (
                            <ScreenshotButton
                                key={screenshotTarget.targetId}
                                compact
                                targetId={screenshotTarget.targetId}
                                fileName={screenshotTarget.fileName}
                            />
                        )}
                        {/* "Screen size" preview icon - temporarily disabled, too buggy
                            (blank-screen flashes switching between presets) to leave
                            reachable right now. See ThemeContext.js's own comment (forces
                            previewWidth to 0 at the source) and this file's own commented-out
                            availableResolutionPresets/cyclePreviewWidth block above. Restore
                            both to bring it back:
                        {!isAuthorized && !isPreviewFrame() && availableResolutionPresets.length > 1 && (
                            <a
                                href="#"
                                className={`action-button matches-btn${previewWidth ? " active" : ""}`}
                                onClick={(e) => { e.preventDefault(); cyclePreviewWidth(); }}
                                title={`${t("Screen size")}: ${t(activeResolutionPreset.label)}`}
                            >
                                <FeatherIcon icon={activeResolutionPreset.icon} size={16} />
                            </a>
                        )} */}
                        <div className="menu-container">
                            <ul className="absolute_menu mode">
                                <li className="second">
                                    <TournamentMenu
                                        id={tournamentId}
                                        user={user}
                                        tournament={tournament}
                                        hideShareExport
                                        isAuthorized={isAuthorized}
                                        theme={theme}
                                        setTheme={setTheme}
                                        accent={accent}
                                        setAccent={setAccent}
                                        compactTable={compactTable}
                                        setCompactTable={setCompactTable}
                                        language={language}
                                        handleLanguageChange={handleLanguageChange}
                                    />
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <table style={{ width: "100%" }}>
                    <tbody>
                    <tr>
                        <td style={{ width: "110px", verticalAlign: "middle" }}>
                            {tournament ? (
                                data.club_logo
                                    ? <img
                                          src={data.club_logo}
                                          className="thumbnail big avatar-zoomable"
                                          alt={data.club_name || data.name}
                                          onClick={() => setLightboxSrc(data.club_logo)}
                                      />
                                    : <div className="no-thumbnail big initials">{getInitials(data.club_name || data.name || "")}</div>
                            ) : (
                                <div style={{ display: "flex" }}>
                                    <Skeleton circle width={100} height={100} />
                                </div>
                            )}
                        </td>
                        <td style={{ verticalAlign: "top" }}>
                            <table className="player_modal" style={{ width: "100%" }}>
                                <tbody>
                                <tr>
                                    <td><label>{t("Date")}</label></td>
                                    <td>{tournament ? (formatDateRange(data.start_date, data.end_date) || "—") : <Skeleton width="70%" />}</td>
                                </tr>
                                <tr>
                                    <td><label>{t("Name")}</label></td>
                                    <td>{tournament ? (data.name || "—") : <Skeleton width="90%" />}</td>
                                </tr>
                                <tr>
                                    <td><label>{t("Club")}</label></td>
                                    <td>
                                        {tournament ? (
                                            data.club_name
                                                ? <a href="#" onClick={openClubModal} title={t("Club page")} className="tournament-header-club-link">
                                                    <span className="tournament-header-club-name">{data.club_name}</span>
                                                    <FeatherIcon icon="external-link" size={11} />
                                                </a>
                                                : "—"
                                        ) : <Skeleton width="70%" />}
                                    </td>
                                </tr>
                                <tr>
                                    <td><label>{t("Location")}</label></td>
                                    <td>
                                        {tournament ? (
                                            locationLabel
                                                ? (data.location
                                                    ? <a href="#" onClick={openLocationModal} title={t("Location")} className="tournament-header-club-link">
                                                        <span className="tournament-header-club-name">{locationLabel}</span>
                                                        <FeatherIcon icon="external-link" size={11} />
                                                    </a>
                                                    : locationLabel)
                                                : "—"
                                        ) : <Skeleton width="80%" />}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ border: 0 }}><label>{t("Rating")}</label></td>
                                    <td style={{ border: 0 }}>
                                        {tournament ? (
                                            data.rating
                                                ? <a href="#" onClick={(e) => { e.preventDefault(); openTournamentRating(); }} title={t("Rating")} className="tournament-header-club-link">
                                                    <span className="tournament-header-club-name">{ratingName || t("Rating")}</span>
                                                    <FeatherIcon icon="external-link" size={11} />
                                                </a>
                                                : t("No rating linked")
                                        ) : <Skeleton width="50%" />}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                    </tbody>
                </table>
                {factChips.length > 0 && (
                    <div className="hero-fact-chip-strip">
                        {factChips.map((chip, i) => (
                            <span key={i} className={`hero-fact-chip t-status-badge-sm inline ${chip.badgeClass}`}>
                                {chip.icon === "ball" ? <BallIconMono /> : <FeatherIcon icon={chip.icon} />}
                                {chip.text}
                            </span>
                        ))}
                    </div>
                )}
                <div ref={heroSentinelRef} style={{ height: "1px" }} />
                <PhotoLightbox src={lightboxSrc} alt={data.club_name || data.name} onClose={() => setLightboxSrc(null)} />
            </div>
            <TournamentShareModal
                isOpen={isShareOpen}
                onClose={() => setIsShareOpen(false)}
                tournamentId={tournamentId}
                tournament={tournament}
            />
            <TournamentExportModal
                isOpen={isExportOpen}
                onClose={() => setIsExportOpen(false)}
                tournamentId={tournamentId}
                tournament={tournament}
            />
        </div>
    );
};

export default TournamentHero;
